import { useSyncExternalStore } from 'react'

/** auto: autopilot inside the Controller section, the scroll path everywhere else. manual: the visitor is piloting. */
export type ControlMode = 'auto' | 'manual'
export type ViewPreset = 'side' | 'front' | 'top' | 'quarter'
export type SpeedStep = 0 | 1 | 2

/** 'controller' while the vehicle is free to fly inside the Controller section's stage. */
export type ControlZone = 'controller' | 'path'

export interface ControlState {
    mode: ControlMode
    lights: boolean
    scan: boolean
    wake: boolean
    speed: SpeedStep
    pad: boolean
    zone: ControlZone
}

const initialState: ControlState = {
    mode: 'auto',
    lights: true,
    scan: false,
    wake: true,
    speed: 1,
    pad: false,
    zone: 'path'
}

let state: ControlState = initialState

const listeners = new Set<() => void>()

export const getControl = (): ControlState => state

export const setControl = (patch: Partial<ControlState>): void => {
    let changed = false

    for (const key of Object.keys(patch) as Array<keyof ControlState>) {
        if (state[key] !== patch[key]) changed = true
    }

    if (!changed) return

    state = { ...state, ...patch }
    listeners.forEach(listener => listener())
}

export const subscribeControl = (listener: () => void): (() => void) => {
    listeners.add(listener)

    return () => {
        listeners.delete(listener)
    }
}

export const useControl = <T>(selector: (value: ControlState) => T): T => {
    return useSyncExternalStore(
        subscribeControl,
        () => selector(state),
        () => selector(initialState)
    )
}

interface Axis {
    x: number
    y: number
}

const axis = (): Axis => ({ x: 0, y: 0 })

/**
 * Per-frame values shared between the 3D remote, keyboard, gamepad and the drone stage.
 * Never put these in React state: they change every frame.
 *
 * Stick convention: x is right, y is up, both in -1..1.
 * Left stick: y climbs or descends, x turns. Right stick: y pitches forward, x rolls.
 */
export const live = {
    /** Stick deflection from dragging the 3D sticks (springs back on release). */
    dragL: axis(),
    dragR: axis(),
    /** Keyboard targets. */
    keyTurn: 0,
    keyLift: 0,
    keyPitch: 0,
    keyRoll: 0,
    keyTriggerL: 0,
    keyTriggerR: 0,
    /** Gamepad axes and triggers. */
    gpL: axis(),
    gpR: axis(),
    gpTriggerL: 0,
    gpTriggerR: 0,
    /** Combined visitor input; the flight model reads these while piloting. */
    stickL: axis(),
    stickR: axis(),
    /** The autopilot's own slow loop, shared so the remote's sticks move in sync with the vehicle. */
    autopilot: { lx: 0, ly: 0, rx: 0, ry: 0, dial: 0, tl: 0, tr: 0 },
    /** performance.now() / 1000 of the last visitor input, for the idle hand-back. */
    lastInput: 0,
    /** Engine time of the last stat-rail step; the drone's forward LiDAR window pulses once from it. */
    lidarPulseAt: -10,
    /** Normalised altitude target the vehicle springs toward, driven by the left stick. */
    altitude: 0,
    viewNonce: 0,
    view: null as ViewPreset | null,
    returnNonce: 0,
    syncAltitude: true,
    /** Hand turn of the vehicle at the footer: drag to spin it, it coasts on release and eases home when the footer leaves. */
    spin: { yaw: 0, pitch: 0, velocity: 0, dragging: false, active: false },
    /** Kept for the terrain scene; stays at rest now that flying happens in one section. */
    camera: { yaw: 0, pitch: 0, dolly: 0, lift: 0 },
    telemetry: {
        heading: 0,
        pitch: 0,
        roll: 0,
        speed: 0,
        moving: false,
        x: 0,
        y: 0,
        size: 0,
        vx: 0,
        vy: 0
    }
}

export const VIEW_ORDER: Array<ViewPreset> = ['side', 'front', 'top', 'quarter']

/** Seconds of no input before the autopilot takes the vehicle back. */
export const IDLE_HANDBACK = 10

export const markInput = (): void => {
    live.lastInput = performance.now() / 1000
}

export const setMode = (mode: ControlMode): void => {
    if (mode === 'manual' && state.mode !== 'manual') live.syncAltitude = true

    setControl({ mode })
}

/** The visitor touched a control: hand the vehicle over, but only inside the flight stage. */
export const takeControl = (): void => {
    markInput()

    if (state.zone === 'controller' && state.mode !== 'manual') setMode('manual')
}

export const requestView = (view: ViewPreset): void => {
    takeControl()
    live.view = view
    live.viewNonce += 1
}

export const cycleView = (): void => {
    const index = live.view ? VIEW_ORDER.indexOf(live.view) : -1

    requestView(VIEW_ORDER[(index + 1) % VIEW_ORDER.length])
}

/** Glides the vehicle back to the centre of the stage, levels it, then hands back to the autopilot. */
export const returnToPath = (): void => {
    live.returnNonce += 1
    live.view = null
    setMode('auto')
}

export const shiftSpeed = (direction: -1 | 1): void => {
    markInput()
    setControl({ speed: Math.min(2, Math.max(0, state.speed + direction)) as SpeedStep })
}

export const SPEED_FACTORS = [0.55, 1, 1.65]
export const SPEED_LABELS = ['Slow', 'Cruise', 'Fast']
